import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Select } from '@dreamtree-org/twreact-ui';
import { useAppApi } from '@/lib/api';
import Can from '@rbac/Can';
import { writeActionFor } from '@rbac/writeAction';
import DataTable from '@/components/DataTable';
import NewWorkflowDialog from '@/components/workflow/NewWorkflowDialog';
import PageShell from '@/components/layout/PageShell';
import PageHeader from '@/components/layout/PageHeader';

/**
 * "Workflows" list — user-authored graphs of LLM calls, gates and tool calls
 * (services/workflows/). Mirrors DynamicToolsPage: the same run/success rollup columns,
 * because a workflow row carries the same counters a dynamic tool row does.
 */
function StatusBadge({ active }) {
  return <Badge variant={active ? 'success' : 'secondary'} size="sm">{active ? 'active' : 'inactive'}</Badge>;
}

const STATUS_TONE = { done: 'text-emerald-700', error: 'text-red-700' };

/** Whether the LAST run succeeded, which is the question an operator actually asks —
 *  the lifetime success ratio sits in its own column. */
function LastStatus({ status }) {
  if (!status) return <span className="text-secondary-500">—</span>;
  return <span className={`text-xs font-medium ${STATUS_TONE[status] || 'text-amber-700'}`}>{status}</span>;
}

/** What the workflow is authored as: a declarative LLM App spec (compiled to its graph
 *  server-side) or a hand-built visual graph. The kind decides which editor surface the
 *  builder opens — surfaced here so an LLM App is identifiable without opening it. */
function KindBadge({ workflow }) {
  if (workflow.spec_json) return <Badge variant="info" size="sm">LLM App</Badge>;
  return <span className="text-xs text-secondary-500">Graph</span>;
}

const COLUMNS = [
  {
    key: 'name',
    label: 'Name',
    render: (w) => (
      <div>
        <div className="font-medium text-primary-600">{w.name}</div>
        <div className="text-secondary-500 text-xs max-w-xs truncate" title={w.description || w.key}>{w.description || w.key}</div>
      </div>
    ),
  },
  { key: 'kind', label: 'Kind', searchable: false, render: (w) => <KindBadge workflow={w} /> },
  { key: 'is_active', label: 'Status', searchable: false, render: (w) => <StatusBadge active={!!w.is_active} /> },
  { key: 'version', label: 'Version', searchable: false, render: (w) => `v${w.version || 1}` },
  { key: 'run_count', label: 'Runs', searchable: false, render: (w) => w.run_count || 0 },
  { key: 'success_count', label: 'Success', searchable: false, render: (w) => (w.run_count ? `${w.success_count || 0}/${w.run_count}` : '—') },
  { key: 'last_status', label: 'Last run', searchable: false, render: (w) => <LastStatus status={w.last_status} /> },
  { key: 'last_run_at', label: 'When', searchable: false, render: (w) => (w.last_run_at ? new Date(w.last_run_at).toLocaleString() : 'never') },
];

export default function WorkflowsPage() {
  const { fetchApi } = useAppApi();
  const nav = useNavigate();
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [creating, setCreating] = useState(false);
  const [apps, setApps] = useState([]);
  const [applicationId, setApplicationId] = useState('');

  const reload = useCallback(() => {
    setLoading(true);
    return fetchApi('crud', { model: 'AppWorkflow', action: 'list', limit: 100 })
      .then((res) => setWorkflows((Array.isArray(res) ? res : res?.data) || []))
      .catch((ex) => setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    fetchApi('applicationsList')
      .then((res) => setApps((Array.isArray(res) ? res : res?.data) || []))
      .catch(() => setApps([]));
  }, []);

  const filteredWorkflows = applicationId
    ? workflows.filter(w => w.application_id == null || w.application_id === Number(applicationId))
    : workflows;

  return (
    <PageShell className="space-y-3">
      <PageHeader
        title="Workflows"
        subtitle={(
          <>
            {workflows.length} workflow{workflows.length === 1 ? '' : 's'} — fixed multi-step procedures agents can run on demand.
          </>
        )}
        actions={(
          <Can module="workflows" action={writeActionFor('workflows')}>
            <Button size="sm" onClick={() => setCreating(true)}>New workflow</Button>
          </Can>
        )}
      />

      <DataTable
        data={filteredWorkflows}
        columns={COLUMNS}
        loading={loading}
        error={err}
        emptyText="No workflows yet."
        search={{ placeholder: 'Filter workflows…', keys: ['name', 'key', 'description'] }}
        onRowClick={(w) => nav(`/app-workflow/${w.id}`)}
        onReload={reload}
        toolbar={(
          <div className="w-48 sm:w-56">
            <Select options={[{ label: 'All applications', value: '' }, ...apps.map(a => ({ label: a.name, value: String(a.id) }))]} value={applicationId} onChange={setApplicationId} placeholder="All applications" searchable />
          </div>
        )}
      />

      {creating && (
        <NewWorkflowDialog
          onClose={() => setCreating(false)}
          onCreated={(newId) => nav(`/app-workflow/${newId}`)}
        />
      )}
    </PageShell>
  );
}
